import { useFormContext, useFieldArray } from 'react-hook-form';
import { Box, Button, Grid, Stack, SxProps, Theme } from '@mui/material';
import type { NewCustomer } from '@vy/types';
import React, { useEffect } from 'react';
import type { CreateCustomerMode } from '../../../create-customer-page-types/CreateCustomerModeType';
import { isEmptyArray } from '@vy/utils';
import { UpdateCustomerWithExtendedContactsField } from '../../../../UpdateCustomerSettingsPage/update-customer-settings-hooks/types/update-customer-with-extended-contacts';
import { CustomerContractItem } from './CustomerContractItem';
import { useContactsSectionManagement } from '../../../create-customer-page-hooks/useContactsSectionManagement';
import { CreateCustomerContactsManagementControls } from './CreateCustomerContactsManagementControls';
import ArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useShowMoreOrLess } from '../../../../../../hooks/useShowMoreOrLess';
import { VyFormSection } from '../../../../../../components/design/layout';
import { VyIcon } from '@vy/ui-design-system';
import { colors } from '../../../../../../assets/themes/colors.ts';

export const CreateCustomerContactsSection: React.FC<CreateCustomerMode> = ({ mode: _mode = 'create' }) => {
  const { control } =
    useFormContext<typeof _mode extends 'create' ? NewCustomer : UpdateCustomerWithExtendedContactsField>();
  const { fields, prepend, remove } = useFieldArray({ control, name: 'contacts' });

  const { isShowLessModeActive, toggleItemsDisplayAmount, processShowMoreOrLess, minDisplayAmount } =
    useShowMoreOrLess();

  const {
    selectedContactsIds,
    isManageModeEnabled,
    toggleManageMode,
    handleContactSelectionToggle,
    handleSelectAllContacts,
    checkIsContactSelected,
    handleRemoveFromSelectedContacts,
    handleDeselectAllContacts,
  } = useContactsSectionManagement(fields);

  const handleAddContact = () =>
    prepend({
      name: '',
      email: '',
      title: '',
      phone: '',
      receiveInvoiceEmail: isEmptyArray(fields),
      isFromDb: false,
    });

  const handlePhoneKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'];

    if (allowedKeys.includes(e.key)) {
      return;
    }

    if (!/[\d+]/.test(e.key)) {
      e.preventDefault();
      return;
    }
  };

  const handlePhoneInput = (e: React.FormEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    if (input.value.length > 14) {
      input.value = input.value.slice(0, 14);
    }
  };

  const handleRemoveContact = (id: string, index: number) => {
    remove(index);
    handleRemoveFromSelectedContacts([id]);
  };

  const handleRemoveSelectedContacts = () => {
    const selectedIdsList = [...selectedContactsIds];
    const indexesToRemove = selectedIdsList.map((id) => fields.findIndex((field) => field.id === id));

    remove(indexesToRemove);
    handleRemoveFromSelectedContacts(selectedIdsList);
  };

  useEffect(() => {
    if (!isManageModeEnabled) {
      handleDeselectAllContacts();
    }

    if (isManageModeEnabled && fields.length === 0) {
      toggleManageMode();
    }
  }, [fields.length, isManageModeEnabled]);

  const controlsSx: SxProps<Theme> =
    fields.length
      ? { position: 'absolute', right: 24, transform: 'translateY(-15%)' }
      : {
        marginLeft: 'auto',
      }

  return (
    <VyFormSection
      title="Contacts"
      icon={
        <VyIcon icon="CustomersUser" width={19} height={18} viewBox="0 0 25 24" color={colors.grey[90]} />
      }
      sx={{ position: 'relative' }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Box sx={controlsSx}>
          <CreateCustomerContactsManagementControls
            handleAddContact={handleAddContact}
            handleToggleManageMode={() => toggleManageMode()}
            handleSelectAllContacts={handleSelectAllContacts}
            isManageModeEnabled={isManageModeEnabled}
            selectedContactsAmount={selectedContactsIds.size}
            handleRemoveSelectedContacts={handleRemoveSelectedContacts}
            totalContactsAmount={fields.length}
            handleDeselectAllContacts={handleDeselectAllContacts}
          />
        </Box>
      </Box>
      <Grid container rowSpacing={3}>
        {processShowMoreOrLess(fields).map((field, index) => (
          <CustomerContractItem
            key={field.id}
            field={field}
            index={index}
            handlePhoneKeyDown={handlePhoneKeyDown}
            handlePhoneInput={handlePhoneInput}
            remove={handleRemoveContact}
            isManageModeEnabled={isManageModeEnabled}
            isSelected={checkIsContactSelected(field.id)}
            handleContactSelectionToggle={() => handleContactSelectionToggle(field.id)}
          />
        ))}
      </Grid>
      {fields.length > minDisplayAmount && (
        <Stack
          direction="row"
          sx={{
            justifyContent: "center",
            alignItems: "center",
            marginTop: 2
          }}>
          <Button
            onClick={() => toggleItemsDisplayAmount(fields)}
            startIcon={isShowLessModeActive ? <ArrowDownIcon /> : <ArrowUpIcon />}
            variant="outlined"
            size="small"
          >
            {isShowLessModeActive ? `Show ${fields.length - minDisplayAmount} more` : 'Show less'}
          </Button>
        </Stack>
      )}
    </VyFormSection>
  );
};
